import { mulberry32, seedFrom } from "./random.js";
export const EMPTY = -1;
/**
 * Ten levels: a 3×3, then a 4×4, then a 6×6 in shapes, then the last two again in digits.
 *
 * **It opens on a 3×3 now, and that is the fix for the commonest complaint about this game.** It
 * used to start at a 4×4 — sixteen squares, fifteen of them already filled, one hole. That is a
 * gentle PUZZLE and a bewildering first SCREEN: a child meeting sudoku for the first time has to
 * find the one empty square among sixteen before they can start thinking, and most of them simply
 * looked at it. Nine squares is a board a five-year-old takes in at a glance, which is what a first
 * level is for.
 *
 * A 3×3 has no boxes, and it does not need any. Its "box" is declared as one row so the generic
 * constraint check has something to read, which makes it a Latin square: one of each in every row
 * and every column, exactly what the game's own hint has always promised. Boxes arrive with the
 * 4×4, where they are the first genuinely new rule rather than the third thing to learn at once.
 *
 * The ceiling is still a 6×6. A 9×9 gives cells around 38 px on a phone, and it is not a preschool
 * activity in any case — a full one takes an adult ten minutes, which is longer than this whole
 * sitting is meant to be.
 */
function recipeFor(difficulty) {
    // Three symbols, no box: the box is the row, so the check has something to read and the rule a
    // child is learning is only "one of each along, one of each down".
    const three = { size: 3, boxCols: 3, boxRows: 1 };
    const four = { size: 4, boxCols: 2, boxRows: 2 };
    const six = { size: 6, boxCols: 3, boxRows: 2 };
    switch (difficulty) {
        case 1:
            return { ...three, blanks: 1, digits: false };
        case 2:
            return { ...three, blanks: 3, digits: false };
        case 3:
            return { ...four, blanks: 2, digits: false };
        case 4:
            return { ...four, blanks: 4, digits: false };
        case 5:
            return { ...six, blanks: 3, digits: false };
        case 6:
            return { ...six, blanks: 8, digits: false };
        case 7:
            return { ...four, blanks: 4, digits: true };
        case 8:
            return { ...six, blanks: 6, digits: true };
        case 9:
            return { ...six, blanks: 10, digits: true };
        default:
            return { ...six, blanks: 14, digits: true };
    }
}
export const SUDOKU_MIN_DIFFICULTY = 1;
export const SUDOKU_MAX_DIFFICULTY = 10;
export function generateSudoku(task, formId, trialIndex, difficulty) {
    if (difficulty < SUDOKU_MIN_DIFFICULTY || difficulty > SUDOKU_MAX_DIFFICULTY) {
        throw new Error(`difficulty ${difficulty} out of range for ${task.id}`);
    }
    const recipe = recipeFor(difficulty);
    const rand = mulberry32(seedFrom(formId, trialIndex, difficulty));
    const solution = solvedGrid(recipe, rand);
    const { given, blanks } = punchHoles(solution, recipe, rand);
    return {
        size: recipe.size,
        boxCols: recipe.boxCols,
        boxRows: recipe.boxRows,
        given,
        solution,
        blanks,
        digits: recipe.digits,
    };
}
/**
 * A complete, valid grid.
 *
 * Built by solving an empty grid with the candidate order shuffled, rather than by permuting a
 * canonical Latin square. Permuting rows and columns of a fixed grid produces puzzles that are
 * all the same puzzle in disguise — a child would meet the same arrangement repeatedly with the
 * shapes moved around, which is the one thing a seeded generator must not do.
 */
function solvedGrid(recipe, rand) {
    const grid = new Array(recipe.size * recipe.size).fill(EMPTY);
    if (!fill(grid, 0, recipe, rand)) {
        // Unreachable for 4×4 and 6×6: both are always completable from empty. Thrown rather than
        // returned partially filled, because a grid with a hole in it would present as a puzzle
        // with no solution and the child would be marked wrong for every square.
        throw new Error(`could not build a ${recipe.size}×${recipe.size} grid`);
    }
    return grid;
}
function fill(grid, at, recipe, rand) {
    if (at >= grid.length)
        return true;
    const candidates = shuffled(recipe.size, rand);
    for (const value of candidates) {
        if (!allowed(grid, at, value, recipe))
            continue;
        grid[at] = value;
        if (fill(grid, at + 1, recipe, rand))
            return true;
        grid[at] = EMPTY;
    }
    return false;
}
/**
 * Removes squares one at a time, keeping only removals that leave the solution unique.
 *
 * The order of removal is shuffled, so two trials at the same level do not hollow out the same
 * squares. The count is a target rather than a guarantee: on a small grid a run of candidate
 * squares can all fail the uniqueness check, and stopping short is correct — a puzzle with one
 * fewer blank is easier than advertised, while a puzzle with two solutions is broken.
 */
function punchHoles(solution, recipe, rand) {
    const given = [...solution];
    const blanks = [];
    const order = shuffled(solution.length, rand);
    for (const cell of order) {
        if (blanks.length >= recipe.blanks)
            break;
        const kept = given[cell];
        given[cell] = EMPTY;
        if (countSolutions(given, recipe, 0, 2) === 1) {
            blanks.push(cell);
        }
        else {
            given[cell] = kept;
        }
    }
    return { given, blanks: blanks.sort((a, b) => a - b) };
}
/**
 * How many ways the grid can be completed, counting no further than `cap`.
 *
 * Capped because the only question ever asked is "is it more than one", and an under-constrained
 * grid has thousands. Without the cap this is the one place in the generator that could take
 * long enough to notice.
 */
function countSolutions(grid, recipe, from, cap) {
    let at = from;
    while (at < grid.length && grid[at] !== EMPTY)
        at++;
    if (at >= grid.length)
        return 1;
    const working = [...grid];
    let found = 0;
    for (let value = 0; value < recipe.size; value++) {
        if (!allowed(working, at, value, recipe))
            continue;
        working[at] = value;
        found += countSolutions(working, recipe, at + 1, cap - found);
        working[at] = EMPTY;
        if (found >= cap)
            return found;
    }
    return found;
}
/** Whether `value` may go at `at`: not already in the row, the column, or the box. */
function allowed(grid, at, value, recipe) {
    const { size, boxCols, boxRows } = recipe;
    const row = Math.floor(at / size);
    const col = at % size;
    for (let c = 0; c < size; c++) {
        if (c !== col && grid[row * size + c] === value)
            return false;
    }
    for (let r = 0; r < size; r++) {
        if (r !== row && grid[r * size + col] === value)
            return false;
    }
    const boxTop = Math.floor(row / boxRows) * boxRows;
    const boxLeft = Math.floor(col / boxCols) * boxCols;
    for (let r = boxTop; r < boxTop + boxRows; r++) {
        for (let c = boxLeft; c < boxLeft + boxCols; c++) {
            const i = r * size + c;
            if (i !== at && grid[i] === value)
                return false;
        }
    }
    return true;
}
/** `0..count-1`, seeded Fisher-Yates. */
function shuffled(count, rand) {
    const out = Array.from({ length: count }, (_, i) => i);
    for (let i = out.length - 1; i > 0; i--) {
        const j = Math.floor(rand() * (i + 1));
        [out[i], out[j]] = [out[j], out[i]];
    }
    return out;
}
/**
 * Refused placements allowed before the round counts as unsolved.
 *
 * **A completed grid is always correct, and that is why this exists.** The renderer refuses a symbol
 * that contradicts the row, the column or the box, so a child cannot leave a wrong value on the
 * board — which means "finished" would be true of every round that ended, and accuracy would carry
 * no signal at all.
 *
 * What differs between children is how many times they TRIED something impossible. A child working
 * by elimination is refused rarely; one tapping symbols until something sticks is refused constantly.
 * The budget is generous — twice the number of blanks — because being refused is how the game
 * teaches, and the point is to bound guessing rather than to punish thinking.
 */
export const refusalBudget = (puzzle) => puzzle.blanks.length * 2;
/**
 * Every blank filled with the value the unique solution has there, without exhausting the budget.
 *
 * `filled` is the whole grid as the child left it, so a square they changed twice is scored on
 * what it ended as — which is the same rule the build-copy game uses, and the only one a child
 * would expect.
 */
export function scoreSudokuTrial(puzzle, filled, refusals = 0) {
    if (refusals > refusalBudget(puzzle))
        return false;
    return puzzle.blanks.every((cell) => filled[cell] === puzzle.solution[cell]);
}
/**
 * Whether a value can be placed at a blank without contradicting what is on the grid.
 *
 * Used by the renderer to refuse an illegal placement instead of accepting it and scoring it
 * wrong later. That is the teaching behaviour: the child finds out that this shape cannot go
 * here at the moment they try it, which is the entire lesson of the game (R-5).
 */
export function placementAllowed(puzzle, grid, cell, value) {
    return allowed(grid, cell, value, {
        size: puzzle.size,
        boxCols: puzzle.boxCols,
        boxRows: puzzle.boxRows,
        blanks: 0,
        digits: puzzle.digits,
    });
}
//# sourceMappingURL=sudoku.js.map